import { CloudCredService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/cloud_cred_connect";
import { CloudInfoService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/cloud_info_connect";
import { InstanceService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/instance_connect";
import type { CloudCred } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/cloud_cred_pb";
import type { InstanceType } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/cloud_info_pb";
import type {
  GPUInstanceType,
  InstanceCloudCred,
} from "types/InstanceTypes";
import type { AxiosRes, BaseRes } from "server/config";
import { createConnectClient } from "server/connect";
import { mapInstanceType } from "server/devplane/instanceTypes/mappers";
import { isActiveCloudCred } from "utils/cloudCreds";

const cloudCredClient = createConnectClient(CloudCredService);
const cloudInfoClient = createConnectClient(CloudInfoService);
const instanceClient = createConnectClient(InstanceService);

type CloudCredError = {
  cloudCred: InstanceCloudCred;
  error: string;
};

type AllInstanceTypeObj = {
  allInstanceTypes: GPUInstanceType[];
  cloudCredErrors: CloudCredError[];
};

type AllInstanceTypeObjRes = {
  data?: AllInstanceTypeObj;
} & AxiosRes &
  BaseRes;

type InstanceTypeListRes = {
  data?: GPUInstanceType[];
} & AxiosRes &
  BaseRes;

const defaultListOptions = {
  includePrices: true,
  includeUnavailable: false,
  includePreemptible: false,
  includeCpu: true,
  uniqueInstanceType: true,
  skipAccessFilter: false,
};

export interface AvailableInstanceTypeOptions {
  skipAccessFilter?: boolean;
  uniqueInstanceType?: boolean;
}

export const getAllPublicInstanceTypesFromDevplane =
  async (): Promise<AllInstanceTypeObjRes> => {
    try {
      const [instanceTypesRes, cloudCredRes] = await Promise.all([
        instanceClient.listPublicInstanceType({
          options: defaultListOptions,
        }),
        cloudCredClient.listPublicCloudCreds({}),
      ]);
      return ok(mapInstanceTypes(instanceTypesRes.items, cloudCredRes.items));
    } catch (error) {
      return fail(
        "Failed to fetch public instance types from dev-plane",
        error,
      );
    }
  };

export const getAllInstanceTypesAvailableFromDevplane = async (
  orgId: string,
  _imageID?: string,
  listOptions: AvailableInstanceTypeOptions = {},
): Promise<AllInstanceTypeObjRes> => {
  try {
    const cloudCredRes = await cloudCredClient.listCloudCredsForOrg({ orgId });
    const cloudCreds = cloudCredRes.items.filter(isActiveCloudCred);
    const cloudCredIds = cloudCreds
      .map((cloudCred) => cloudCred.cloudCredId)
      .filter(Boolean);

    if (cloudCredIds.length === 0) {
      return ok([]);
    }

    const res = await instanceClient.listInstanceType({
      cloudCredIds,
      options: {
        ...defaultListOptions,
        uniqueInstanceType:
          listOptions.uniqueInstanceType ??
          defaultListOptions.uniqueInstanceType,
        skipAccessFilter:
          listOptions.skipAccessFilter ?? defaultListOptions.skipAccessFilter,
        organizationId: orgId,
      },
    });

    return ok(mapInstanceTypes(res.items, cloudCreds));
  } catch (error) {
    return fail("Failed to fetch org instance types from dev-plane", error);
  }
};

export const getInstanceByTypeForCloudCredFromDevplane = async (
  instanceType: string,
  cloudCredId: string,
  location?: string | null,
): Promise<InstanceTypeListRes> => {
  try {
    const res = await cloudInfoClient.listInstanceType({
      cloudCredId,
      // Pin the price to this instance's actual configured location.
      ...(location ? { location } : {}),
      options: {
        ...defaultListOptions,
        includePrices: true,
        includeUnavailable: true,
        instanceTypes: [instanceType],
      },
    });

    return {
      success: true,
      data: mapInstanceTypes(res.items, [], cloudCredId),
    };
  } catch (error) {
    return failInstanceTypeList(
      "Failed to fetch instance type from dev-plane",
      error,
    );
  }
};

const ok = (allInstanceTypes: GPUInstanceType[]): AllInstanceTypeObjRes => ({
  success: true,
  data: {
    allInstanceTypes,
    cloudCredErrors: [],
  },
});

const fail = (message: string, error: unknown): AllInstanceTypeObjRes => {
  console.error(message, error);
  return {
    success: false,
    message:
      error instanceof Error && error.message
        ? `${message}: ${error.message}`
        : message,
  };
};

const failInstanceTypeList = (
  message: string,
  error: unknown,
): InstanceTypeListRes => {
  console.error(message, error);
  return {
    success: false,
    message:
      error instanceof Error && error.message
        ? `${message}: ${error.message}`
        : message,
  };
};

export const mapInstanceTypes = (
  instanceTypes: InstanceType[],
  cloudCreds: CloudCred[] = [],
  fallbackCloudCredId?: string,
): GPUInstanceType[] => {
  const cloudCredById = new Map(
    cloudCreds.map((cloudCred) => [cloudCred.cloudCredId, cloudCred]),
  );

  return instanceTypes.map((instanceType) => {
    const mappedInstanceType = mapInstanceType(instanceType);
    const raw = mappedInstanceType as unknown as Record<string, any>;
    const directCloudCred = (raw.cloud_cred || raw.cloudCred) as
      | Record<string, any>
      | undefined;
    const cloudCredId =
      raw.cloud_cred_id ||
      raw.cloudCredId ||
      instanceType.cloudCredId ||
      fallbackCloudCredId;
    const cloudCred =
      cloudCredById.get(instanceType.cloudCredId) ??
      cloudCredById.get(cloudCredId) ??
      (fallbackCloudCredId
        ? cloudCredById.get(fallbackCloudCredId)
        : undefined);
    const instanceCloudCred = mapInstanceCloudCred(
      instanceType,
      raw,
      cloudCred,
      directCloudCred,
      cloudCredId,
    );
    const cloudCredEntries = instanceCloudCred ? [instanceCloudCred] : [];

    const locationPrices = getLocationPrices(
      instanceType.locationPrices,
      raw.location_prices,
      raw.locationPrices,
    );

    return {
      ...mappedInstanceType,
      available_locations: getStringList(
        raw.available_locations,
        raw.availableLocations,
      ),
      available_sublocations: getStringList(
        raw.available_sublocations,
        raw.availableSublocations,
      ),
      ...(locationPrices ? { location_prices: locationPrices } : {}),
      cloud_cred_id: cloudCredId,
      cloud_cred: directCloudCred ?? mapCloudCredToJson(cloudCred),
      cloud_creds: cloudCredEntries,
    } as GPUInstanceType;
  });
};

const mapInstanceCloudCred = (
  instanceType: InstanceType,
  rawInstanceType: Record<string, any>,
  cloudCred?: CloudCred,
  directCloudCred?: Record<string, any>,
  fallbackCloudCredId?: string,
): InstanceCloudCred | undefined => {
  const cloudCredId =
    cloudCred?.cloudCredId ||
    directCloudCred?.cloud_cred_id ||
    directCloudCred?.cloudCredId ||
    rawInstanceType.cloud_cred_id ||
    rawInstanceType.cloudCredId ||
    instanceType.cloudCredId ||
    fallbackCloudCredId;

  if (!cloudCredId) {
    return undefined;
  }

  const providerId =
    cloudCred?.providerId ||
    directCloudCred?.provider_id ||
    directCloudCred?.providerId ||
    rawInstanceType.provider ||
    rawInstanceType.cloud ||
    "";
  const platformRegion =
    cloudCred?.platformRegion ||
    directCloudCred?.platform_region ||
    directCloudCred?.platformRegion ||
    rawInstanceType.location ||
    "";
  const availableLocations = getStringList(
    rawInstanceType.available_locations,
    rawInstanceType.availableLocations,
  );
  const locationNames =
    availableLocations.length > 0
      ? availableLocations
      : getStringList(rawInstanceType.location, platformRegion);

  return {
    id: cloudCredId,
    name: cloudCred?.name || directCloudCred?.name || providerId || cloudCredId,
    status: mapStatus(cloudCred?.status || directCloudCred?.status),
    platform: "devplane",
    platformId: cloudCredId,
    platformRegion,
    platformType: providerId,
    tenantType: mapTenantType(
      cloudCred?.tenantType ??
        directCloudCred?.tenant_type ??
        directCloudCred?.tenantType,
    ),
    locations: locationNames.map((name) => ({
      name,
      description: name,
      available:
        rawInstanceType.is_available ?? rawInstanceType.isAvailable ?? true,
    })),
  };
};

export const getLocationPrices = (
  ...values: unknown[]
): GPUInstanceType["location_prices"] | undefined => {
  for (const value of values) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      continue;
    }

    const normalized: NonNullable<GPUInstanceType["location_prices"]> = {};
    for (const [location, priceValue] of Object.entries(
      value as Record<string, unknown>,
    )) {
      if (!priceValue || typeof priceValue !== "object") {
        continue;
      }
      const price = priceValue as Record<string, unknown>;
      const amount = price.amount ?? price.Amount;
      if (amount == null || amount === "") {
        continue;
      }
      normalized[location] = {
        currency: String(price.currency ?? price.Currency ?? "USD"),
        amount: String(amount),
      };
    }

    if (Object.keys(normalized).length > 0) {
      return normalized;
    }
  }

  return undefined;
};

export const getStringList = (...values: unknown[]): string[] => {
  const seen = new Set<string>();
  const strings: string[] = [];

  for (const value of values) {
    const valuesToAdd = Array.isArray(value) ? value : [value];
    for (const valueToAdd of valuesToAdd) {
      if (typeof valueToAdd !== "string") {
        continue;
      }
      const trimmed = valueToAdd.trim();
      if (!trimmed || seen.has(trimmed)) {
        continue;
      }
      seen.add(trimmed);
      strings.push(trimmed);
    }
  }

  return strings;
};

const mapCloudCredToJson = (
  cloudCred?: CloudCred,
): GPUInstanceType["cloud_cred"] | undefined => {
  if (!cloudCred) {
    return undefined;
  }
  return {
    cloud_cred_id: cloudCred.cloudCredId,
    provider_id: cloudCred.providerId,
    labels: cloudCred.labels,
    status: cloudCred.status,
    name: cloudCred.name,
    platform_region: cloudCred.platformRegion,
    tenant_type: mapTenantType(cloudCred.tenantType),
  };
};

export const mapStatus = (status?: string) => {
  if (!status || status === "active") {
    return "RUNNING";
  }
  return status.toUpperCase();
};

export const mapTenantType = (tenantType?: string | number) => {
  if (tenantType === 1 || tenantType === "TENANT_TYPE_SHARED") {
    return "shared";
  }
  if (tenantType === 2 || tenantType === "TENANT_TYPE_ISOLATED") {
    return "isolated";
  }
  if (typeof tenantType === "string" && tenantType.length > 0) {
    return tenantType.toLowerCase();
  }
  return "shared";
};
